import { Calendar, ExternalLink, FileIcon } from 'lucide-react'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Button } from '@/components/ui/button'
import { GoogleDriveMetadata } from '@/services/inference.gen'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import dayjs from 'dayjs'
import GoogleDriveLogo from '/assets/image/google-drive.svg'
import { Progress } from '@/components/ui/progress'

type GoogleDriveDetailsProps = {
  TriggerButton: React.ReactNode
  googleDriveMetadata: GoogleDriveMetadata[]
}

export const GoogleDriveDetails = ({
  TriggerButton,
  googleDriveMetadata = [],
}: GoogleDriveDetailsProps) => {
  if (!googleDriveMetadata?.length) return null

  const metadata = googleDriveMetadata[0]
  const status = metadata.status || 'syncing'
  const isSuccess = status === 'success'
  const isFailed = status === 'failed'
  const isSyncing = !isSuccess && !isFailed
  const progressValue = metadata.progress || 0

  return (
    <Dialog>
      <DialogTrigger asChild>{TriggerButton}</DialogTrigger>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <img src={GoogleDriveLogo} alt="Google Drive" className="w-6 h-6" />
            <DialogTitle>Google Drive Sync Details</DialogTitle>
          </div>
        </DialogHeader>

        <ScrollArea className="max-h-[460px]">
          <div className="space-y-3">
            <div className="rounded-lg border p-4 bg-card shadow-sm">
              <div className="flex items-start gap-2">
                <ExternalLink className="h-4 w-4 text-primary mt-1" />
                <div className="flex flex-col">
                  <span className="font-semibold">File Source:</span>
                  <a
                    href={metadata.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:text-primary/80 underline break-all text-sm"
                  >
                    {metadata.url}
                  </a>
                </div>
              </div>
            </div>

            <div className="rounded-lg border p-4 bg-card shadow-sm">
              <div className="flex items-start gap-2">
                <FileIcon className="h-4 w-4 text-primary mt-1" />
                <div className="flex flex-col">
                  <span className="font-semibold">Mount Path:</span>
                  <span className="break-all">{metadata.file_path || '/'}</span>
                </div>
              </div>
            </div>

            <div className="rounded-lg border p-4 bg-card shadow-sm">
              <div className="flex items-start gap-2">
                <div className="mt-1 h-4 w-4 flex items-center justify-center">
                  <div
                    className={cn(
                      'w-3 h-3 rounded-full',
                      isSuccess
                        ? 'bg-primary'
                        : isFailed
                          ? 'bg-destructive'
                          : 'bg-muted'
                    )}
                  />
                </div>
                <div className="flex flex-col w-full">
                  <div className="flex items-center">
                    <span className="font-semibold">Sync Status:</span>
                    <Badge
                      variant={
                        isSuccess
                          ? 'default'
                          : isFailed
                            ? 'destructive'
                            : 'secondary'
                      }
                      className="ml-2"
                    >
                      {status}
                    </Badge>
                  </div>
                  {isSyncing && (
                    <div className="mt-2 space-y-1 w-full">
                      <Progress value={progressValue} className="h-1.5" />
                      <span className="text-xs text-muted-foreground text-right">
                        {Math.round(progressValue)}%
                      </span>
                    </div>
                  )}
                  {metadata.msg && (
                    <span className="text-sm text-muted-foreground break-all mt-1">
                      {metadata.msg}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded-lg border p-4 bg-card shadow-sm">
              <div className="flex items-start gap-2">
                <Calendar className="h-4 w-4 text-primary mt-1" />
                <div className="flex flex-col">
                  <span className="font-semibold">Last Synced:</span>
                  <span>
                    {metadata.synced_at
                      ? dayjs(metadata.synced_at).format(
                          'MMMM DD, YYYY [at] HH:mm:ss'
                        )
                      : 'Not synced yet'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>

        <DialogFooter className="pt-2">
          <DialogClose asChild>
            <Button variant="secondary">Close</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
